import {
  AUDIT_LOGS_DEFAULT_PAGE_SIZE,
  AUDIT_LOGS_PAGE_SIZE_OPTIONS,
} from "../types";
import type {
  AuditLogAction,
  AuditLogSource,
  AuditLogsQueryParams,
} from "../types";

/**
 * In-app filter state. Superset of `AuditLogsQueryParams`:
 *   - `actions` is always an array (UI uses multi-select).
 *   - `page` and `limit` are required and have safe defaults.
 *
 * The same shape is used by the URL parser, the filters bar, and the
 * list query — so there's a single source of truth.
 */
export interface AuditLogFiltersState {
  productId: number | null;
  resourceType: string | null;
  actions: AuditLogAction[];
  source: AuditLogSource | null;
  from: string | null;
  to: string | null;
  userEmail: string;
  search: string;
  page: number;
  limit: number;
}

/** Default state used both for "blank slate" and for "Clear filters". */
export const DEFAULT_AUDIT_LOG_FILTERS: AuditLogFiltersState = {
  productId: null,
  resourceType: null,
  actions: [],
  source: null,
  from: null,
  to: null,
  userEmail: "",
  search: "",
  page: 1,
  limit: AUDIT_LOGS_DEFAULT_PAGE_SIZE,
};

const ALLOWED_ACTIONS: ReadonlySet<string> = new Set<AuditLogAction>([
  "create",
  "update",
  "delete",
  "associate",
  "dissociate",
  "bulk_update",
  "claim",
]);

const ALLOWED_SOURCES: ReadonlySet<string> = new Set<AuditLogSource>([
  "dashboard",
  "pipeline",
  "admin",
  "system",
]);

function parseInteger(raw: string | null): number | null {
  if (raw == null || raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && Number.isInteger(n) ? n : null;
}

function parseIsoDate(raw: string | null): string | null {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function parseActions(raw: string | null): AuditLogAction[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => ALLOWED_ACTIONS.has(s)) as AuditLogAction[];
}

function parseSource(raw: string | null): AuditLogSource | null {
  if (!raw || !ALLOWED_SOURCES.has(raw)) return null;
  return raw as AuditLogSource;
}

function parseLimit(raw: string | null): number {
  const n = parseInteger(raw);
  if (n != null && AUDIT_LOGS_PAGE_SIZE_OPTIONS.includes(n)) return n;
  return AUDIT_LOGS_DEFAULT_PAGE_SIZE;
}

function parsePage(raw: string | null): number {
  const n = parseInteger(raw);
  return n != null && n >= 1 ? n : 1;
}

/**
 * Build a typed filters object from `URLSearchParams`. Unknown / invalid
 * values fall back to the default for that field — never throws — so a
 * hand-edited URL can't break the page.
 */
export function parseAuditLogFiltersFromSearchParams(
  searchParams: URLSearchParams
): AuditLogFiltersState {
  return {
    productId: parseInteger(searchParams.get("productId")),
    resourceType: (searchParams.get("resourceType") || null) as string | null,
    actions: parseActions(searchParams.get("action")),
    source: parseSource(searchParams.get("source")),
    from: parseIsoDate(searchParams.get("from")),
    to: parseIsoDate(searchParams.get("to")),
    userEmail: searchParams.get("userEmail") ?? "",
    search: searchParams.get("search") ?? "",
    page: parsePage(searchParams.get("page")),
    limit: parseLimit(searchParams.get("limit")),
  };
}

/**
 * Inverse of `parseAuditLogFiltersFromSearchParams`. Returns a plain
 * object with only the keys whose value differs from the default — keeps
 * the URL short and `?page=1&limit=25` from polluting links.
 *
 * Use with `setSearchParams(serialize(state), { replace: true })`.
 */
export function serializeAuditLogFilters(
  state: AuditLogFiltersState
): Record<string, string> {
  const out: Record<string, string> = {};

  if (state.productId != null) out.productId = String(state.productId);
  if (state.resourceType) out.resourceType = state.resourceType;
  if (state.actions.length > 0) out.action = state.actions.join(",");
  if (state.source) out.source = state.source;
  if (state.from) out.from = state.from;
  if (state.to) out.to = state.to;
  if (state.userEmail.trim() !== "") out.userEmail = state.userEmail.trim();
  if (state.search.trim() !== "") out.search = state.search.trim();
  if (state.page !== DEFAULT_AUDIT_LOG_FILTERS.page) {
    out.page = String(state.page);
  }
  if (state.limit !== DEFAULT_AUDIT_LOG_FILTERS.limit) {
    out.limit = String(state.limit);
  }

  return out;
}

/**
 * Convert the UI filter state into the exact payload expected by
 * `fetchAuditLogs` (drops UI-only fields, keeps backend ones).
 *
 * Insertion order matters: Axios serializes query string keys in
 * insertion order, and the backend addendum asks for `tenantId` to come
 * first when present (consistency with the rest of the dashboard).
 * `MainAuditLogs` injects `tenantId` first, then this function appends
 * the rest — keeping the assembled query like
 * `?tenantId=…&productId=…&action=…`.
 */
export function buildAuditLogsQueryParams(
  state: AuditLogFiltersState
): AuditLogsQueryParams {
  const out: AuditLogsQueryParams = {};
  if (state.productId != null) out.productId = state.productId;
  if (state.resourceType) out.resourceType = state.resourceType;
  if (state.actions.length > 0) out.action = state.actions;
  if (state.source) out.source = state.source;
  if (state.from) out.from = state.from;
  if (state.to) out.to = state.to;
  if (state.userEmail.trim() !== "") out.userEmail = state.userEmail.trim();
  if (state.search.trim() !== "") out.search = state.search.trim();
  out.page = state.page;
  out.limit = state.limit;
  return out;
}

/** True when ANY user-applied filter is active (page/limit don't count). */
export function hasActiveAuditLogFilters(
  state: AuditLogFiltersState
): boolean {
  return (
    state.productId != null ||
    state.resourceType != null ||
    state.actions.length > 0 ||
    state.source != null ||
    state.from != null ||
    state.to != null ||
    state.userEmail.trim() !== "" ||
    state.search.trim() !== ""
  );
}
